<template>
    <ToastContainer />
    <section class="page">
        <SidebarComponent />
        <EventDashboardHeader />
        <section class="interactive">
            <FilterBar
                :items="filterItems"
                @apply="fetchEvents"
                @change="onFilterSelect"
                class="dashboard-filterbar"
                page="event-log"
                :enable-views="enableViews"
            />
            <ag-grid-vue
                class="ag-theme-playvox"
                :column-defs="columnDefs"
                :default-col-def="defaultColDef"
                :on-grid-ready="onGridReady"
                :row-data="rowData"
                :master-detail="true"
                :detail-cell-renderer-params="detailCellRendererParams"
                :dom-layout="domLayout"
                :pagination="true"
                :pagination-page-size="100"
                :on-first-data-rendered="onFirstDataRendered"
                :no-rows-overlay-component="NoRowsOverlay"
                :no-rows-overlay-component-params="noRowsOverlayParams"
            />
        </section>
    </section>
</template>
<script lang="ts">
import type { FilterBar } from '@agyletime/vue-3-common/src/typings/filter';
import { EventCount, SelectedEvents } from './loadDependencies';
import { SingleSelectItem } from '@agyletime/vue-3-common/src/components/FilterBar/utils/definitions/SelectItem';

type MultiSelectValue = FilterBar.MultiSelectValue;
type MultiSelectItem = FilterBar.MultiSelectItem;
type SelectItemValue = FilterBar.SelectItemValue;

// https://www.ag-grid.com/vue-data-grid/column-properties/
type DefaultColumnDefsType = {
    // column
    field?: string;
    type?: string | string[];
    checkboxSelection?: boolean;
    showDisabledCheckboxes?: boolean;
    // Display
    hide?: boolean;
    initialHide?: boolean;
    // Editing
    editable?: boolean;
    // Filter
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filter?: any;
    // Header
    autoHeaderHeight?: boolean;
    suppressMenu?: boolean;
    // Pinned
    pinned?: boolean | 'left' | 'right' | null;
    initialPinned?: boolean | 'left' | 'right';
    lockPinned?: boolean;
    // Rendering and Styling
    autoHeight?: boolean;
    wrapText?: boolean;
    // Sort
    sortable?: boolean;
    sort?: 'asc' | 'desc' | null;
    initialSort?: 'asc' | 'desc' | null;
    // Width
    width?: number;
    initialWidth?: number;
    minWidth?: number;
    maxWidth?: number;
    flex?: number;
    initialFlex?: number;
    resizable?: boolean;
    suppressSizeToFit?: boolean;
    suppressAutoSize?: boolean;
};
type ColumnDefsType = {
    id: string;
    field: string;
    headerName: string;
    dataType: string;
    cellRenderer?: string;
    sortable?: boolean;
    resizable?: boolean;
    filter?: string;
    width?: number;
    flex?: number;
};
</script>
<script setup lang="ts">
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { LicenseManager } from 'ag-grid-enterprise';
import { onMounted, ref } from 'vue';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { AgGridVue } from 'ag-grid-vue3';
import {
    EventDashboardHeader,
    NoRowsOverlay
} from '../index';
import {
    GetDetailRowDataParams,
    TData,
    TDetail,
    GridApi,
    GridReadyEvent,
    IDetailCellRendererParams
} from 'ag-grid-community';
import { useWindowSize } from '../../composables/useWindowSize';
import { mapFieldsFromEventSpecificData } from '../../utils/mapFieldsFromEventSpecificData';
import {
    generateFilters,
    generateFilteredEvents
} from '../../utils/filterUtils';
import { getEventLogs } from '../../utils/getEventLogs';
import { getEventCounts, getStartOfWeekConfig } from './loadDependencies';
import { setupFilterItems } from './filter';
import * as Toast from '@agyletime/vue-3-common/src/components/Toast/toastState';
import { ToastContainer } from '@agyletime/vue-3-common/src/components/Toast';

const enableViews = false;
/* eslint-disable-next-line max-len */
LicenseManager.setLicenseKey('Using_this_{AG_Grid}_Enterprise_key_{AG-061879}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Agyle_Time}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Agyle_Time}_only_for_{16}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Agyle_Time}_need_to_be_licensed___{Agyle_Time}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{17_August_2025}____[v3]_[01]_MTc1NTM4NTIwMDAwMA==90d29b2021b34d8e10945fb8a84b5315');

let g_eventTypes = <EventCount[]>[];
let g_weekStart = <0 | 1 | 2 | 3 | 4 | 5 | 6>1;
const filterItems = ref<MultiSelectItem[]>([]);
// Defining the filters applied by the user
let selectedEventTypes = <MultiSelectValue>{};
let selectedEventServices = <MultiSelectValue>{};
let selectedEventLevels = <MultiSelectValue>{};
let selectedTimezone = <SelectItemValue>{};
let selectedDates = <SelectItemValue[]>[];

// Reference to the column definition
const columnDefs = ref<ColumnDefsType[] | []>([]);
const defaultColDef = ref<DefaultColumnDefsType>({ resizable: true });
const gridApi = ref<GridApi | null>(null);
const retrievedEvents = ref<Record<string, unknown>[]>([]);
const rowData = ref<Record<string, unknown>[]>([]);
// Initial message displayed when "no row" is called.
// This message will be overwritten on "fetchEvents"
let noRowsOverlayMessage = ref<string>('No results found, please apply a filter or adjust the search criteria.');
const noRowsOverlayParams = { noRowsMessageFunc: () => noRowsOverlayMessage.value };
const parseJsonToObject = (obj: Record<string, string>) => {
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            obj[key] = JSON.stringify(obj[key]);
        }
    }
    return obj;
};
const detailCellRendererParams = (
    params: GetDetailRowDataParams<TData, TDetail>
): IDetailCellRendererParams => {
    return {
        detailGridOptions: {
            columnDefs: mapFieldsFromEventSpecificData(
                params.data.eventSpecificData
            ),
            defaultColDef: {
                flex: 1,
                resizable: true
            }
        },
        getDetailRowData: (params: GetDetailRowDataParams<TData, TDetail>) => {
            const { eventSpecificData } = params.data;

            if (Array.isArray(eventSpecificData)) {
                eventSpecificData.forEach((eventSpecific) => {
                    parseJsonToObject(eventSpecific);
                });

                params.successCallback([...params.data.eventSpecificData]);
            } else {
                parseJsonToObject(eventSpecificData);
                params.successCallback([params.data.eventSpecificData]);
            }
        }
    } as IDetailCellRendererParams;
};

const onGridReady = async (evt: GridReadyEvent) => {
    gridApi.value = evt.api;
    gridApi.value?.showNoRowsOverlay();
    columnDefs.value = [
        {
            id: 'eventDate',
            field: 'eventDate',
            headerName: 'Timestamp',
            dataType: 'date',
            sortable: true,
            cellRenderer: 'agGroupCellRenderer'
        },
        {
            id: 'eventType',
            field: 'eventType',
            headerName: 'Event Type',
            dataType: 'string',
            filter: 'agTextColumnFilter'
        },
        {
            id: 'eventMessage',
            field: 'eventMessage',
            headerName: 'Message',
            dataType: 'string',
            filter: 'agTextColumnFilter',
            flex: 1
        },
        {
            id: 'eventService',
            field: 'eventService',
            headerName: 'Affected Service',
            dataType: 'string',
            filter: 'agTextColumnFilter'
        },
        {
            id: 'eventLevel',
            field: 'eventLevel',
            headerName: 'Level',
            dataType: 'string',
            filter: 'agTextColumnFilter'
        }
    ];
};
const domLayout = 'normal';
const handleResize = () => {
    gridApi?.value?.sizeColumnsToFit();
};
/**
 * Called by the `FilterBar` when an option in one of the menu is selected.
 * `change` event is emitted.
 * @param evt: The full object selected
 */
// eslint-disable-next-line complexity
const onFilterSelect = async (evt: SelectedEvents) => {
    if (!g_eventTypes || g_eventTypes.length === 0) {
        return;
    }
    selectedEventTypes = evt.eventTypes as MultiSelectValue;
    selectedEventServices = evt.eventServices as MultiSelectValue;
    selectedEventLevels = evt.eventLevels as MultiSelectValue;
    selectedTimezone = evt.timezone as SingleSelectItem;
    selectedDates = evt.startDate as SingleSelectItem[];

    // Apply filters and aggregation
    const filteredEvents = generateFilteredEvents(g_eventTypes);
    const { eventTypeFilters, eventLevelFilters, eventServiceFilters } =
        generateFilters(filteredEvents, evt);
    filterItems.value = setupFilterItems(
        eventTypeFilters,
        eventLevelFilters,
        eventServiceFilters,
        g_weekStart
    );
};

/**
 * Called by the `FilterBar` when the user click on 'Apply' button.
 * Set the "loading" spinner for the grid, get the events from the backend, using the filter(s).
 * This call resets the pagination.
 * @param evt: The selected filter(s)
 */
const fetchEvents = async (evt: SelectedEvents) => {
    gridApi?.value?.showLoadingOverlay();
    selectedEventTypes = evt.eventTypes;
    selectedEventServices = evt.eventServices;
    selectedEventLevels = evt.eventLevels;
    selectedTimezone = evt.timezone;
    try {
        const eventLogs = await getEventLogs({
            eventTypes: selectedEventTypes,
            eventLevels: selectedEventLevels,
            eventServices: selectedEventServices,
            timezone: selectedTimezone,
            startDate: selectedDates?.[0] ?? undefined,
            endDate: selectedDates?.[1] ?? undefined
        });
        retrievedEvents.value = eventLogs.data;
        rowData.value = eventLogs.data;
    } catch (error) {
        Toast.addToast( `error.${error.message}`, `An error occured while loading the data: ${error.message}`, 'ERROR');
    }
};

const onFirstDataRendered = () => {
    gridApi?.value?.sizeColumnsToFit();
};
useWindowSize(handleResize);
onMounted(async () => {
    const result = await Promise.allSettled([
        getEventCounts(),
        getStartOfWeekConfig()
    ]);
    g_eventTypes = result[0].value;
    g_weekStart = result[1].value?.filterData?.weekStart;
    // Initial filter is empty, so all events will be available
    const filteredEvents = generateFilteredEvents(g_eventTypes);
    const { eventTypeFilters, eventLevelFilters, eventServiceFilters } =
        generateFilters(filteredEvents, {});
    filterItems.value = setupFilterItems(
        eventTypeFilters,
        eventLevelFilters,
        eventServiceFilters,
        g_weekStart
    );
});
</script>
<style lang="scss">
@use '@/styles/commonPageStyles.scss';
@use './style.scss';
</style>
